import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Form, Input, Button, Alert, Card, Typography } from 'antd';
import Ink from 'react-ink';
import { accountId, api } from '../../config';

import './login.css';

import {
    userLogin,
    userResetPasswordOTP
} from '../../modules/user';

import { MailOutlined, LockOutlined } from '@ant-design/icons';

const FormItem = Form.Item;
const { Text } = Typography;

class LoginCard extends Component {

    formRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            message: null,
            messageType: null,
            showOTP: false,
            email: '',
            password: '',
            showForgotPassword: false,
            resetPasswordStep: 1, // 1: email, 2: OTP, 3: new password
            resetOTP: '',
        }
    }

    handleSubmit = (values) => {
        const { userSessionTimeout } = this.props;
        const { showOTP, showForgotPassword, resetPasswordStep } = this.state;
        this.setState({ loading: true });
        
        // Handle forgot password flow
        if (showForgotPassword) {
            // Step 1: Request OTP with email
            if (resetPasswordStep === 1) {
                const { email } = values;
                this.setState({ email });
                
                this.props.userResetPasswordOTP({ email }).then(
                    () => {
                        // This should not happen as we expect OTP error
                    },
                    (err) => {
                        if (err?.response?.data?.message === "OTP is required") {
                            this.setState({
                                loading: false,
                                resetPasswordStep: 2,
                                message: "A verification code has been sent to your email. It will expire in 5 minutes.",
                                messageType: "info",
                            });
                            if (this.formRef.current) {
                                this.formRef.current.resetFields(['otp']);
                            }
                        } else {
                            this.setState({
                                loading: false,
                                message: err?.response?.data?.message || "Failed to request password reset.",
                                messageType: 'error',
                            });
                        }
                    }
                );
                return;
            }
            
            // Step 2: Verify OTP
            if (resetPasswordStep === 2) {
                const { otp } = values;
                const { email } = this.state;
                
                this.setState({ resetOTP: otp });
                
                this.props.userResetPasswordOTP({ email, OTP: otp }).then(
                    () => {
                        this.setState({
                            loading: false,
                            resetPasswordStep: 3,
                            message: "OTP verified. Please set your new password.",
                            messageType: "success",
                        });
                        if (this.formRef.current) {
                            this.formRef.current.resetFields(['password', 'confirmPassword']);
                        }
                    },
                    (err) => {
                        this.setState({
                            loading: false,
                            message: err?.response?.data?.message || "Invalid verification code. Please try again.",
                            messageType: "error",
                        });
                    }
                );
                return;
            }
            
            // Step 3: Set new password
            if (resetPasswordStep === 3) {
                const { password } = values;
                const { email, resetOTP } = this.state;
                
                if (password !== values.confirmPassword) {
                    this.setState({
                        loading: false,
                        message: "Passwords do not match",
                        messageType: "error",
                    });
                    return;
                }
                
                this.props.userResetPasswordOTP({ email, OTP: resetOTP, password }).then(
                    () => {
                        this.setState({
                            loading: false,
                            message: "Password reset successful. Please login with your new password.",
                            messageType: "success",
                            showForgotPassword: false,
                            resetPasswordStep: 1,
                        });
                        if (this.formRef.current) {
                            this.formRef.current.resetFields();
                        }
                    },
                    (err) => {
                        this.setState({
                            loading: false,
                            message: err?.response?.data?.message || "Failed to reset password. Please try again.",
                            messageType: "error",
                        });
                    }
                );
                return;
            }
        }
        
        // Regular login flow
        if (!showOTP) {
            this.setState({
                email: values.email,
                password: values.password
            });
        }

        const apiDomain = api;
        localStorage.setItem('apiDomain', apiDomain);
        
        // If OTP is showing, include it in the request
        const loginData = showOTP 
            ? { email: this.state.email, password: this.state.password, OTP: values.otp }
            : values;
            
        this.props.userLogin(loginData).then(
            (res) => {
                this.setState({ loading: false, message: null });
                if (this.formRef.current) {
                    this.formRef.current.resetFields();
                }
                if(!userSessionTimeout){
                    window.parent.location = `${window.parent.location.origin}`;
                }
                this.goBackToLogin();
            },
            (err) => {
                console.log('err', err.response);
                // Check if the error is related to OTP
                if (err?.response?.data?.message === "OTP is required") {
                    this.setState({ 
                        loading: false, 
                        showOTP: true, 
                        message: "A verification code has been sent to your email. It will expire in 5 minutes.",
                        messageType: "info",
                    });
                    if (this.formRef.current) {
                        this.formRef.current.resetFields(['password']);
                    }
                } else {
                    this.setState({ 
                        loading: false, 
                        message: `Please check Username/Password and try again.`,
                        messageType: "error",
                     });
                }
            }
        );
    }

    resendOTP = () => {
        const { email, password, showForgotPassword } = this.state;
        this.setState({ loading: true });
        
        if (showForgotPassword) {
            this.props.userResetPasswordOTP({ email }).then(
                () => {
                    // This should fail with OTP required
                },
                (err) => {
                    if (err?.response?.data?.message === "OTP is required") {
                        this.setState({ 
                            loading: false, 
                            message: "A new verification code has been sent to your email. It will expire in 5 minutes.",
                            messageType: "info",
                        });
                        if (this.formRef.current) {
                            this.formRef.current.resetFields(['otp']);
                        }
                    } else {
                        this.setState({ 
                            loading: false, 
                            message: `Error resending verification code. Please try again.`,
                            messageType: "error",
                        });
                    }
                }
            );
            return;
        }
        
        this.props.userLogin({ email, password }).then(
            () => {
                // This should fail with OTP required
            },
            (err) => {
                if (err?.response?.data?.message === "OTP is required") {
                    this.setState({ 
                        loading: false, 
                        message: "A new verification code has been sent to your email. It will expire in 5 minutes.",
                        messageType: "info",
                    });
                    if (this.formRef.current) {
                        this.formRef.current.resetFields(['otp']);
                    }
                } else {
                    this.setState({ 
                        loading: false, 
                        message: `Error resending verification code. Please try again.`,
                        messageType: "error",
                    });
                }
            }
        );
    }

    goBackToLogin = () => {
        this.setState({ 
            showOTP: false, 
            showForgotPassword: false,
            resetPasswordStep: 1,
            message: null,
        });
        if (this.formRef.current) {
            this.formRef.current.resetFields();
        }
    }
    
    showForgotPasswordForm = () => {
        this.setState({
            showForgotPassword: true,
            showOTP: false,
            resetPasswordStep: 1,
            message: null,
        });
        if (this.formRef.current) {
            this.formRef.current.resetFields();
        }
    }

    render() {
        const { loading, message, messageType, showOTP, showForgotPassword, resetPasswordStep } = this.state;
        const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);
        
        return (
            <Card className="loginHolder">
                <h3 className="title"><LockOutlined /> {showForgotPassword ? "Reset Password" : "Sign In"} - {capitalizeFirstLetter(accountId)}</h3>
                <Form ref={this.formRef} name="myForm" onFinish={this.handleSubmit} className="login-form">
                    <div className="contentHolder">
                        {(message) ? <Alert message={message} type={messageType || "error"} style={{ marginBottom: '20px' }} showIcon /> : ""}
                        
                        {showForgotPassword ? (
                            resetPasswordStep === 1 ? (
                                // Step 1: Enter Email
                                <FormItem name='email' rules={[
                                    { type: 'email', message: 'Please input a valid E-mail!' },
                                    { required: true, message: 'Please input your email!' }
                                ]}>
                                    <Input 
                                        style={{ borderLeft: "0px", borderRight: "0px", borderTop: "0px" }} 
                                        size="large" 
                                        prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} 
                                        placeholder="E-mail" 
                                    />
                                </FormItem>
                            ) : resetPasswordStep === 2 ? (
                                // Step 2: Enter OTP
                                <div className="otpContainer">
                                    <Text>Enter the verification code sent to your email</Text>
                                    <FormItem name='otp' rules={[{ required: true, message: 'Please input the verification code!' }]}>
                                        <Input.OTP style={{ marginTop: "15px" }} size="large" length={6} />
                                    </FormItem>
                                    <Button type="link" onClick={this.resendOTP} disabled={loading}>
                                        Resend verification code
                                    </Button>
                                </div>
                            ) : (
                                // Step 3: Set New Password
                                <>
                                    <FormItem name='password' rules={[{ required: true, message: 'Please input your new password!' }]}>
                                        <Input 
                                            style={{ borderLeft: "0px", borderRight: "0px", borderTop: "0px" }} 
                                            size="large" 
                                            prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} 
                                            type="password" 
                                            placeholder="New Password" 
                                        />
                                    </FormItem>
                                    <FormItem name='confirmPassword' rules={[
                                        { required: true, message: 'Please confirm your new password!' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('The two passwords do not match!'));
                                            },
                                        }),
                                    ]}>
                                        <Input 
                                            style={{ borderLeft: "0px", borderRight: "0px", borderTop: "0px" }} 
                                            size="large" 
                                            prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} 
                                            type="password" 
                                            placeholder="Confirm New Password" 
                                        />
                                    </FormItem>
                                </>
                            )
                        ) : showOTP ? (
                            // Login OTP flow
                            <div className="otpContainer">
                                <Text>Enter the verification code sent to your email</Text>
                                <FormItem name='otp' rules={[{ required: true, message: 'Please input the verification code!' }]}>
                                    <Input.OTP style={{ marginTop: "15px" }} size="large" length={6} />
                                </FormItem>
                                <Button type="link" onClick={this.resendOTP} disabled={loading}>
                                    Resend verification code
                                </Button>
                            </div>
                        ) : (
                            // Regular login form
                            <>
                                <FormItem name='email' rules={[{ type: 'email', message: 'Please input a valid E-mail!' },
                                { required: true, message: 'Please input your email!' }]}>
                                    <Input style={{ borderLeft: "0px", borderRight: "0px", borderTop: "0px" }} size="large" prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} placeholder="E-mail" />
                                </FormItem>
                                <FormItem name='password' rules={[{ required: true, message: 'Please input your password!' }]}>
                                    <Input style={{ borderLeft: "0px", borderRight: "0px", borderTop: "0px" }} size="large" prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} type="password" placeholder="Password" />
                                </FormItem>
                            </>
                        )}
                    </div>
                    
                    <FormItem>
                        <div className="btnHolder">
                            <Button type="primary" block htmlType="submit" className="login-form-button" disabled={loading} loading={loading}>
                                <Ink />
                                {showForgotPassword ? (
                                    resetPasswordStep === 1 ? "Request Code" :
                                    resetPasswordStep === 2 ? "Verify Code" : "Reset Password"
                                ) : (showOTP ? "Verify" : "Login")}
                            </Button>
                            
                            {showForgotPassword || showOTP ? (
                                <Button type="link" onClick={this.goBackToLogin} disabled={loading} className='back-button'>
                                    Back to login
                                </Button>
                            ) : (
                                <Button type="link" block onClick={this.showForgotPasswordForm} disabled={loading} className='forgot-password-button'>
                                    Setup / Forgot password?
                                </Button>
                            )}
                        </div>
                    </FormItem>
                </Form>
            </Card>
        );
    }
}

const mapStateToProps = state => ({
    userAuthenticated: state.user.userAuthenticated,
});

const mapDispatchToProps = dispatch => bindActionCreators({
    userLogin,
    userResetPasswordOTP
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(LoginCard);
